import React, { forwardRef, Ref, FocusEvent, ReactNode, JSX } from 'react';
import { useTheme } from '@emotion/react';
import ReactSelect, { SelectInstance as ReactSelectInstance, createFilter } from 'react-select';
import { StylesConfig as ReactSelectStyles, SingleValue } from 'react-select/dist/declarations/src';
import { FormatOptionLabelMeta } from 'react-select/dist/declarations/src/Select';
import { FilterOptionOption } from 'react-select/dist/declarations/src/filters';
import classNames from 'classnames';
import { DataTestIdProp } from '../../../../Models/DataTestIdProp';
import { isApiBase } from '../../../../Utils/typeGuards';
import { MenuPlacement } from 'react-select/dist/declarations/src/types';

// Select does have performance issues with many items. In this case use SelectAsyncPaginate.
// Alternative would be to (re)add: https://github.com/jacobworrel/react-windowed-select/blob/master/src/MenuList.js

export interface SelectProps<Option> {
  selectedOption?: Option | null; // can pass in full object
  onChange: (option: Option) => void; // get full object out
  getValue: (option: Option) => string; // but arbitrary - so you must define how to get a (comparable) value
  getLabel: (option: Option) => string | React.ReactNode; // but arbitrary - so you must define how to get a label
  placeholder?: string;
  noOptionsMessage?: (obj: { inputValue: string }) => ReactNode;
  id?: string | undefined;
  isClearable?: boolean;
  isSearchable?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  isInvalid?: boolean;
  windowThreshold?: number;
  tabSelectsValue?: boolean;
  formatOptionLabel?: (option: Option, labelMeta: FormatOptionLabelMeta<Option>) => React.ReactNode;
  menuPortalTarget?: HTMLElement;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  menuIsOpen?: boolean;
  onFocus?: (e: FocusEvent<HTMLElement>) => void;
  onBlur?: (e: FocusEvent<HTMLElement>) => void;
  inputValue?: string;
  setInputValue?: (value: string) => void;
  filterOption?: (option: FilterOptionOption<Option>, inputValue: string) => boolean;
  ignoreFilterOption?: boolean;
  className?: string;
  autoFocus?: boolean;
  menuPlacement?: MenuPlacement;
}

export interface Props<Option> extends SelectProps<Option>, DataTestIdProp {
  options: Option[]; // arbitrary objects as list
  styles?: ReactSelectStyles<Option>;
  closeMenuOnSelect?: boolean;
  getRefObject?: (ref: ReactSelectInstance<Option>) => void;
  tabIndex?: number;
}

export const Select = <Option extends object>(props: Props<Option>): JSX.Element => {
  const theme = useTheme();

  const {
    selectedOption,
    placeholder,
    noOptionsMessage,
    onChange,
    isInvalid,
    isDisabled,
    isClearable,
    isSearchable,
    id,
    getValue,
    getLabel,
    options,
    tabSelectsValue,
    formatOptionLabel,
    menuPortalTarget = null,
    onMenuOpen,
    onMenuClose,
    filterOption,
    ignoreFilterOption,
    className,
    isLoading,
    setInputValue,
    styles,
    closeMenuOnSelect = true,
    getRefObject,
    inputValue,
    autoFocus,
    dataTestId,
    menuPlacement = 'bottom',
    tabIndex
  } = props;

  const customStyles = styles || (theme.reactSelectStyles as ReactSelectStyles<Option>);
  return (
    <div
      className={classNames({
        'react-select': true,
        'is-invalid': isInvalid,
        'input--filled': !!selectedOption,
        className: !!className
      })}
      data-testid={dataTestId}
    >
      <ReactSelect
        ref={(refObject: ReactSelectInstance<Option>) => {
          if (getRefObject) {
            getRefObject(refObject);
          }
        }}
        instanceId={id}
        theme={theme.reactSelectTheme}
        className={isInvalid ? 'is-invalid' : ''}
        styles={customStyles}
        options={options}
        isClearable={isClearable}
        isSearchable={isSearchable}
        isLoading={isLoading}
        onInputChange={setInputValue}
        value={selectedOption}
        onChange={onChange as (value: SingleValue<Option>) => void}
        getOptionValue={getValue}
        placeholder={placeholder}
        noOptionsMessage={noOptionsMessage}
        tabSelectsValue={tabSelectsValue}
        tabIndex={tabIndex}
        inputValue={inputValue}
        id={id}
        isDisabled={isDisabled}
        classNamePrefix="select"
        formatOptionLabel={formatOptionLabel || getLabel}
        menuPortalTarget={menuPortalTarget}
        menuPosition={menuPortalTarget != null ? 'fixed' : undefined}
        onMenuClose={onMenuClose}
        onMenuOpen={onMenuOpen}
        closeMenuOnSelect={closeMenuOnSelect}
        autoFocus={autoFocus}
        filterOption={
          ignoreFilterOption
            ? null
            : filterOption ||
              createFilter({
                matchFrom: 'any',
                stringify: (option: FilterOptionOption<Option>) =>
                  isApiBase(option.data)
                    ? option.data.description
                    : (option.data as Record<string, string>)[Object.keys(option.data)[0]]
              })
        }
        menuPlacement={menuPlacement}
      />
    </div>
  );
};

function SelectRefWrapper<Option>(
  props: Props<Option> & SelectProps<Option>,
  ref: Ref<ReactSelectInstance<Option>>
): JSX.Element {
  const theme = useTheme();

  const {
    selectedOption,
    placeholder,
    noOptionsMessage,
    onChange,
    isInvalid,
    isDisabled,
    isClearable,
    isSearchable,
    id,
    getValue,
    getLabel,
    options,
    tabSelectsValue,
    formatOptionLabel,
    menuPortalTarget = null,
    onMenuOpen,
    onMenuClose,
    menuPlacement = 'bottom'
  } = props;

  const customStyles = theme.reactSelectStyles as ReactSelectStyles<Option>;

  return (
    <>
      <ReactSelect
        theme={theme.reactSelectTheme}
        className={isInvalid ? 'is-invalid' : ''}
        styles={customStyles}
        options={options}
        isClearable={isClearable}
        isSearchable={isSearchable}
        value={selectedOption}
        onChange={onChange as (value: SingleValue<Option>) => void}
        getOptionValue={getValue}
        placeholder={placeholder}
        noOptionsMessage={noOptionsMessage}
        tabSelectsValue={tabSelectsValue}
        tabIndex={undefined}
        id={id}
        isDisabled={isDisabled}
        classNamePrefix="select"
        formatOptionLabel={formatOptionLabel || getLabel}
        menuPortalTarget={menuPortalTarget}
        menuPosition={menuPortalTarget != null ? 'fixed' : undefined}
        onMenuClose={onMenuClose}
        onMenuOpen={onMenuOpen}
        ref={ref}
        menuPlacement={menuPlacement}
      />
    </>
  );
}

export const SelectRef = forwardRef(SelectRefWrapper) as <T>(
  props: Props<T> & SelectProps<T> & { ref?: React.Ref<ReactSelectInstance<T>> }
) => ReturnType<typeof SelectRefWrapper>;
